/**
 * Lane C0-PROPORTION, BODY-B3-R1 (spec docs/review/art/BODY-B3-R1.md): the bare body's two alternatives per sex, A and B,
 * as a skeleton + shape override on the B3 figure. Review only: off by default, so nothing changes in the game; the
 * review sheet (tools/charLab/bodyMock.ts) turns it on for its rows. figureB3.ts `frameOfB3` passes every B3 skeleton
 * through `bodyMock`, which returns it untouched while the mock is off.
 *
 * What it changes (art px; the spec's px are fine px, two per art px):
 * - the build's half-widths (shoulder, waist, hip) and limb thickness, by the spec's shape-unit guide (a man's hip
 *   brought in with his waist so he tapers to a V instead of pinching like an hourglass);
 * - the shoulder joints out (a man) or in (a woman B), the arm moved with them;
 * - at rest: each hanging arm's elbow off the narrower waist (the negative space between arm and torso) and its hand
 *   beside the hip (a man's in, so his shoulders stay the widest row; a woman's out with her hips);
 * - head-on at rest: the feet
 *   set to the spec's gap with the knees on the hip-to-ankle line (a woman's a touch in), a man B's crotch a pixel
 *   higher, a woman's hips shifted over the weight leg (B also drops the shoulder on that side);
 * - in profile: the torso deeper (chest and back clearly past the neck), the chest a pixel forward of the belly, a woman's waist back (S-curve), the knee softened, the seat.
 * The head joint never moves (the head is locked), and the neck keeps today's width (`mockNeck`).
 */
import { torsoEdges, torsoWidths } from '../figure';
import type { Piece } from '../figure';
import { shade } from '../../palette';
import type { Joint, JointName, Shape, Side, Skeleton } from '../skeleton';

export type BodyVariant = 'A' | 'B';

/**
 * The in-game switch (user pick 10-11: body A for both sexes, the player's figure only). Off until main flips it after
 * the user has seen the dressed sheet: then the player's man and woman are drawn with body A everywhere.
 */
export const BODY_A_IN_GAME = true;
/** The variant drawn when no review tool has chosen one. */
export const BODY_DEFAULT: BodyVariant | null = BODY_A_IN_GAME ? 'A' : null;

let MOCK: BodyVariant | null = BODY_DEFAULT;
/** Review tools: draw the bare body as variant A or B (null: today's body); `BODY_DEFAULT` restores the game's. */
export function setBodyMock(v: BodyVariant | null): void {
  MOCK = v;
}
/**
 * Whether the figure being drawn takes the variant: the player's man and woman (hero looks, adult) only, set by
 * figureB3.ts `frameOfB3` before each frame (every other figure keeps its body).
 */
let TARGET = false;
export function setBodyTarget(v: boolean): void {
  TARGET = v;
}
/** The variant for the figure being drawn now, if any. */
const live = (): BodyVariant | null => (TARGET ? MOCK : null);
export const bodyMockOf = (): BodyVariant | null => live();

/** Review only: slider extremes laid over the variant (art px of half-width; the shoulder joints move with `shoulder`). */
export interface MockAxes {
  shoulder: number;
  waist: number;
  hip: number;
  /** Arm length: +1 a longer hanging arm (the hand 1.5 art px lower), −1 shorter. */
  arm: number;
}
let AXES: MockAxes = { shoulder: 0, waist: 0, hip: 0, arm: 0 };
export function setMockAxes(a: Partial<MockAxes> = {}): void {
  AXES = { shoulder: 0, waist: 0, hip: 0, arm: 0, ...a };
}

interface Spec {
  /** Half-width deltas, art px (after B3's ×0.85). */
  shoulder: number;
  waist: number;
  hip: number;
  limb: number;
  /** Seat fullness (absolute). */
  seat: number;
  /** Each shoulder joint moved out (−: in) head-on, art px; turned views take their share. */
  delt: number;
  /** A hanging arm's elbow and wrist (with the hand) moved out further than the shoulder (−: in), head-on: the elbow
   *  off the narrower waist (the arm gap), the hand beside the hip (a man's in, so his shoulders are the widest row). */
  elbowAdj: number;
  wristAdj: number;
  /** Head-on at rest: each ankle this far from the pelvis's centre line. */
  foot: number;
  /** Knees in from the hip-to-ankle line (a woman's slight inward line). */
  kneeIn: number;
  /** Pelvis and hips raised (longer legs, shorter torso; the soles stay). */
  crotch: number;
  /** Pelvis shifted over the weight leg (A). */
  hipShift: number;
  /** The weight leg's shoulder dropped. */
  shoulderDrop: number;
  /** Profile only: added half-widths (torso depth; figure.ts `torsoWidths` takes ×0.67 / ×0.8 / ×0.83 in profile), so
   *  the chest and back stand clearly deeper than the neck (user 10-11: "the body is thinner than the neck"). */
  depth: readonly [number, number, number];
  /** BODY-B3 R3 M4: the pelvis and hips lowered (art px: a longer torso, shorter legs, same height; the waist half as
   *  far, the knees a third), and the hanging arm shortened (wrist and hand up, the elbow half as far). */
  legShorten: number;
  armShorten: number;
  /** BODY-B3 R3 M3: turned three-quarter, the waist further in and the hip out, so the pinch survives the turn. */
  turnPinch: number;
  /** Profile: chest forward, waist back. */
  chestFwd: number;
  waistBack: number;
}

const SPECS: Record<'m' | 'f', Record<BodyVariant, Spec>> = {
  m: {
    // Lean swordsman: feet 4 fine px apart (2 art), arms a gap off the waist.
    A: { shoulder: 0.5, waist: -0.4, hip: -0.3, limb: 0.5, seat: 1, delt: 1.0, elbowAdj: -0.3, wristAdj: -1.5, foot: 4, kneeIn: 0, crotch: 0, hipShift: 0, shoulderDrop: 0, chestFwd: 1, waistBack: 0, depth: [2.5, 1.0, 1.0], legShorten: 0, armShorten: 0, turnPinch: 0 },
    // Martial build: broader, narrower waist, crotch up, 8-px stance (4 art) with the feet a touch outside the hips.
    B: { shoulder: 1.0, waist: -0.8, hip: -0.6, limb: 0.8, seat: 1, delt: 2.0, elbowAdj: -1.0, wristAdj: -2.5, foot: 4.5, kneeIn: 0, crotch: 1, hipShift: 0, shoulderDrop: 0, chestFwd: 1, waistBack: 0, depth: [3.0, 1.0, 1.2], legShorten: 0, armShorten: 0, turnPinch: 0 },
  },
  f: {
    // Graceful (slimmer): waist in, hips a little out, feet 2 fine px apart, a slight hip shift.
    A: { shoulder: 0, waist: -0.6, hip: 0.5, limb: 0.4, seat: 1, delt: -0.3, elbowAdj: 0.3, wristAdj: 0.8, foot: 3, kneeIn: 0.5, crotch: 0, hipShift: 0.5, shoulderDrop: 0, chestFwd: 0.5, waistBack: 0, depth: [2.2, 1.0, 1.0], legShorten: 2, armShorten: 2.5, turnPinch: 0.8 },
    // Heroine (fuller): narrower shoulders, deeper waist, much fuller hips and seat, hip shift + the opposite shoulder line.
    B: { shoulder: -0.7, waist: -1.0, hip: 2.0, limb: 0, seat: 2, delt: -1.2, elbowAdj: 1.2, wristAdj: 3.2, foot: 3, kneeIn: 0.5, crotch: 0, hipShift: 1, shoulderDrop: 0.5, chestFwd: 0.5, waistBack: 0, depth: [2.5, 0.8, 2.0], legShorten: 0, armShorten: 0, turnPinch: 0.8 },
  },
};

/** The neck width today's shape draws (figure.ts `torso`: 4 for broad shoulders, else 3), kept by the mock. */
const NECK = new WeakMap<Shape, number>();
/** The neck width to keep for a mocked skeleton's shape (figureB3.ts `heroNeck`), or undefined. */
export const mockNeck = (sk: Skeleton): number | undefined => (live() && sk.view === 'side' ? 0 : NECK.get(sk.shape));

const copy = (sk: Skeleton): Record<JointName, Joint> =>
  Object.fromEntries(Object.entries(sk.joints).map(([k, v]) => [k, { ...v }])) as Record<JointName, Joint>;

/** Whether an arm hangs at the side (the wrist below the waist, the hand not raised or held in front). */
const hanging = (j: Record<JointName, Joint>, s: Side): boolean =>
  j[`wrist${s}`].y > j.waist.y + 2 && j[`elbow${s}`].y > j[`shoulder${s}`].y + 4;

/** The B3 skeleton with the bare-body variant applied (unchanged while the mock is off). */
export function bodyMock(sk: Skeleton, body: { female?: boolean; build?: string }): Skeleton {
  const v = live();
  if (!v) return sk;
  const female = !!body.female;
  const base = SPECS[female ? 'f' : 'm'][v];
  const heavy = body.build === 'heavy' || body.build === 'stocky';
  // Slider extremes (BODY-B3 R3 S1-S4): the hip axis widens the hip row ±1.5 (and the thighs, below); a man's wider
  // shoulders bring a little hip with them (no pencil-legged V); a heavy build keeps a waist under the hip (woman) or
  // under the shoulders (man).
  const p: Spec = {
    ...base,
    shoulder: base.shoulder + AXES.shoulder * (female ? 1 : 0.6),
    delt: base.delt + AXES.shoulder,
    waist: base.waist + AXES.waist + (heavy ? (female ? -1.0 : -2.2) : 0),
    hip: base.hip + AXES.hip * 1.5 + (!female && AXES.shoulder > 0 ? AXES.shoulder : 0),
    // A woman's arms keep their place at the waist when her shoulders narrow or widen (the pinch stays open).
    // A heavy man's thicker arms stand further off his waist, so the V and the arm gap survive.
    elbowAdj: base.elbowAdj - (female ? AXES.shoulder : 0) + (heavy && !female ? 1.5 : 0),
    wristAdj: base.wristAdj - (female ? AXES.shoulder : 0) + (heavy && !female ? 1 : 0),
    armShorten: base.armShorten - AXES.arm * 1.5,
  };
  const j = copy(sk);
  const sh0 = sk.shape;
  const shape: Shape = {
    ...sh0,
    shoulder: sh0.shoulder + p.shoulder,
    waist: sh0.waist + p.waist,
    hip: sh0.hip + p.hip,
    limb: sh0.limb + p.limb,
    seat: p.seat,
  };
  if (sk.view === 'side') {
    shape.shoulder += p.depth[0];
    shape.waist += p.depth[1];
    shape.hip += p.depth[2];
  }
  NECK.set(shape, torsoWidths(sk)[0] >= 7 ? 4 : 3);
  const turn = sk.square ? 1 : sk.view === 'side' ? 0.15 : 0.6;
  const idle = sk.pose === 'idle';
  if (p.turnPinch && (sk.view === 'front3' || sk.view === 'back3')) {
    shape.waist -= p.turnPinch;
    shape.hip += p.turnPinch * 0.75;
  }

  // The hip slider moves the thighs too (±1 art px across both).
  if (AXES.hip)
    for (const s of ['A', 'B'] as Side[]) {
      const off = j[`hip${s}`].x - j.pelvis.x;
      if (Math.abs(off) >= 0.5) j[`hip${s}`].x += Math.sign(off) * AXES.hip * 0.5 * turn;
    }

  // A longer torso on shorter legs (the soles stay), and shorter hanging arms.
  if (idle && p.legShorten) {
    for (const n of ['pelvis', 'hipA', 'hipB'] as const) j[n].y += p.legShorten;
    j.waist.y += p.legShorten * 0.75;
    for (const n of ['kneeA', 'kneeB'] as const) j[n].y += p.legShorten / 3;
  }
  if (idle && p.armShorten)
    for (const s of ['A', 'B'] as Side[]) {
      if (!hanging(j, s)) continue;
      j[`elbow${s}`].y -= p.armShorten / 2;
      for (const n of ['wrist', 'hand'] as const) j[`${n}${s}`].y -= p.armShorten;
    }

  // Shoulders out (or in) about the chest, the arm with them.
  for (const s of ['A', 'B'] as Side[]) {
    const off = j[`shoulder${s}`].x - j.chest.x;
    if (Math.abs(off) < 0.5) continue;
    const dir = Math.sign(off) * turn;
    j[`shoulder${s}`].x += dir * p.delt;
    const hang = idle && hanging(j, s);
    j[`elbow${s}`].x += dir * (p.delt + (hang ? p.elbowAdj : 0));
    for (const n of ['wrist', 'hand'] as const) j[`${n}${s}`].x += dir * (p.delt + (hang ? p.wristAdj + AXES.hip * 0.75 : 0));
  }

  // Wider hips carry the thigh tops out with them, so the hip line runs into the thighs instead of a shelf over them.
  if (p.hip > 0)
    for (const s of ['A', 'B'] as Side[]) {
      const off = j[`hip${s}`].x - j.pelvis.x;
      if (Math.abs(off) >= 0.5) j[`hip${s}`].x += Math.sign(off) * p.hip * 0.5 * turn;
    }

  if (idle && sk.square) {
    const cx = j.pelvis.x;
    // Crotch up: the pelvis and hip joints rise, the knees half as much; the soles stay.
    if (p.crotch) {
      for (const n of ['pelvis', 'hipA', 'hipB'] as const) j[n].y -= p.crotch;
      for (const n of ['kneeA', 'kneeB'] as const) j[n].y -= p.crotch / 2;
    }
    // Feet to the spec's gap, the knees on the line from the hip to the ankle (a woman's a touch in).
    for (const s of ['A', 'B'] as Side[]) {
      const sign = Math.sign(j[`hip${s}`].x - cx) || (s === 'A' ? 1 : -1);
      const hip = j[`hip${s}`];
      const knee = j[`knee${s}`];
      const ankle = j[`ankle${s}`];
      ankle.x = cx + sign * p.foot;
      const t = (knee.y - hip.y) / Math.max(1, ankle.y - hip.y);
      knee.x = hip.x + (ankle.x - hip.x) * t - sign * p.kneeIn;
    }
    // The weight on the right leg (A): the pelvis and hips over it, the waist half as far; that side's shoulder drops.
    if (p.hipShift) {
      const sign = Math.sign(j.hipA.x - cx) || 1;
      const d = sign * p.hipShift;
      for (const n of ['pelvis', 'hipA', 'hipB'] as const) j[n].x += d;
      j.waist.x += d / 2;
      // That side's hanging arm moves out with the hip, so the arm gap stays open.
      if (hanging(j, 'A')) for (const n of ['elbow', 'wrist', 'hand'] as const) j[`${n}A`].x += d;
    }
    if (p.shoulderDrop) for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const) j[`${n}A`].y += p.shoulderDrop;
  }

  // Review only: the turned idle's legs (pose data, lane C0-POSE-FIX) splay or cross; the mock stands them neutral,
  // the feet drawn in under the hips (the far foot keeps its step back), so the body reads on its own.
  if (idle && (sk.view === 'front3' || sk.view === 'back3'))
    for (const s of ['A', 'B'] as Side[]) {
      const hip = j[`hip${s}`];
      const ankle = j[`ankle${s}`];
      ankle.x = hip.x + (ankle.x - hip.x) * 0.6;
    }
  if (idle && (sk.view === 'front3' || sk.view === 'back3')) {
    // No enclosed gap between the legs (BODY-B3 R4 W1: feet touching under parted knees left a black oval): a man's feet
    // a clear step apart (6 art px between the ankles, so the feet part too and the legs open in a V); a woman's 1 apart,
    // the far leg a darker plane showing past the near one all the way down (R5-1, `mockFarLeg`).
    const gap = j.ankleA.x - j.ankleB.x;
    const want = female ? 1 : 6;
    const mid = (j.ankleA.x + j.ankleB.x) / 2;
    const sgn = gap >= 0 ? 1 : -1;
    j.ankleA.x = mid + (sgn * want) / 2;
    j.ankleB.x = mid - (sgn * want) / 2;
    for (const s of ['A', 'B'] as Side[]) {
      const hip = j[`hip${s}`];
      const knee = j[`knee${s}`];
      const ankle = j[`ankle${s}`];
      const t = (knee.y - hip.y) / Math.max(1, ankle.y - hip.y);
      knee.x = hip.x + (ankle.x - hip.x) * t;
    }
  }

  if (idle && sk.view === 'side') {
    // The far thigh straight behind the near one (R5-3: its edge showed as a seam through the thigh); below the knee the
    // stride keeps the legs apart.
    const far: Side = j.hipA.z < j.hipB.z ? 'A' : 'B';
    const near: Side = far === 'A' ? 'B' : 'A';
    j[`hip${far}`].x = j[`hip${near}`].x;
    j[`knee${far}`].x = j[`knee${near}`].x;
    // Facing +x (left views are drawn so and mirrored after).
    j.chest.x += p.chestFwd;
    j.waist.x -= p.waistBack;
    for (const s of ['A', 'B'] as Side[]) j[`knee${s}`].x += 0.5;
  }

  const out: Skeleton = { ...sk, joints: j, shape };

  return out;
}

/**
 * In profile the bare torso's top row is as deep as the chest, a flat shelf from the neck to the shoulder's front and
 * back. With the mock on, its corners are rounded off over the first rows below the neck (trapezius into the chest in
 * front, into the shoulder blade behind): the columns kept widen from the neck's by 1.5 px a row.
 */
export function mockProfile(q: Piece, sk: Skeleton): Piece {
  if (!live() || sk.view !== 'side' || q.hand) return q;
  const j = sk.joints;
  const top = Math.round(j.chest.y) - 1;
  const nx = j.neck.x;
  return {
    ...q,
    draw: (g) => {
      g.save();
      g.beginPath();
      g.rect(-1000, -1000, 3000, 3000);
      for (let k = 0; k < 4; k++) {
        const half = 2.5 + 1.5 * k;
        const y = top + k;
        g.rect(-1000, y, 1000 + Math.round(nx - half), 1);
        g.rect(Math.round(nx + half) + 1, y, 1000, 1);
      }
      g.clip('evenodd');
      q.draw(g);
      g.restore();
    },
  };
}

/**
 * The hero's neck in profile with the mock on (user 10-11: "fix the neck in the side view": it ran as one thick block
 * from the whole underside of the skull to the chest, flush with the chin). The skull's grid is left as it is (locked);
 * only its neck part (below the jaw line, behind the jaw's back corner: fine x <= 3, y >= 10 from the head joint) is
 * clipped away, and a slimmer neck drawn in its place at art resolution: leaning forward from the trapezius, the throat
 * set back under the chin (an open angle under the jaw), the nape curving in under the skull and out into the
 * shoulders; the woman's a pixel slimmer. The torso's own neck column is left out (`mockNeck` 0 in profile).
 */
export function mockSkullNeck(q: Piece, sk: Skeleton, sex: 'm' | 'f', skin: { skin: string; skinS: string }): Piece {
  if (!live() || sk.view !== 'side') return q;
  const j = sk.joints;
  const hx = Math.round(j.head.x);
  const hy = Math.round(j.head.y);
  const yb = (Math.round(j.chest.y) - hy) * 2 + 2;
  const f = sex === 'f';
  // Art px from the head joint: [back, front] columns at the top (under the skull), the middle and the base (a man's
  // neck 4 art px deep, a woman's 3, widening into the trapezius behind at the base).
  const back = f ? [-2, -2, -3] : [-2, -2, -3];
  // The throat's top at art x +1 for both (lane FACE-EXPR, user 10-11): the skull is clipped at fine x <= 3 below the
  // jaw line, so a front at 0 left fine x 2..3 empty under the woman's cheek, which the outline drew as a black bar.
  const front = [1, 1, 0];
  const at = (k: readonly number[], t: number) => (t < 0.5 ? k[0]! + (k[1]! - k[0]!) * t * 2 : k[1]! + (k[2]! - k[1]!) * (t - 0.5) * 2);
  const lit = skin.skin;
  const dark = skin.skinS;
  const neck = (g: CanvasRenderingContext2D) => {
    for (let ay = hy + 5; ay * 2 - hy * 2 <= yb; ay++) {
      const y = ay * 2 - hy * 2;
      const t = Math.max(0, Math.min(1, (y - 10) / Math.max(1, yb - 10)));
      const L = hx + Math.round(at(back, t));
      const R = hx + Math.round(at(front, t));
      if (R < L) continue;
      g.fillStyle = lit;
      g.fillRect(L, ay, R - L + 1, 1);
      // The nape's shade line at the back, the throat's light edge at the front.
      g.fillStyle = dark;
      g.fillRect(L, ay, 1, 1);
    }
  };
  const clipped = (g: CanvasRenderingContext2D, x1: number, y0: number, draw: () => void) => {
    g.save();
    g.beginPath();
    g.rect(-4000, -4000, 12000, 12000);
    g.rect(-4000, y0, 4000 + x1 + 1, 4000);
    g.clip('evenodd');
    draw();
    g.restore();
  };
  return {
    ...q,
    draw: (g) => {
      neck(g);
      clipped(g, hx + 1, hy + 5, () => q.draw(g));
    },
    ...(q.fine && {
      fine: (g: CanvasRenderingContext2D, res: number) => clipped(g, hx * 2 + 3, hy * 2 + 10, () => q.fine!(g, res)),
    }),
  };
}

/**
 * Hanging hands at the spec's size for the mock (BODY-B3 R3 M2: the mittens read as 5-6 px fists at 1:1): a man's 8 x 9
 * fine px, a woman's 7 x 8, the thumb notched on the body's side (the facing side in profile), the knuckle row a pixel
 * darker, the fingertips in shade. Drawn at the fine grid over art pixels where the hand fills its block, as the fine
 * hands are. Only hands hanging at the side; any other hand keeps the rig's own. The game's hands are untouched.
 */
const HAND_M = ['.ssssss.', 'sssssssS', 'tssssssS', 'tssssssS', '.ssssssS', '.kkkkkkS', '.ssssss.', '.ssssss.', '..eeee..'];
const HAND_F = ['.sssss.', 'ssssssS', 'tsssssS', '.sssssS', '.kkkkkS', '.sssss.', '.sssss.', '..eee..'];

export function mockHands(pieces: readonly Piece[], sk: Skeleton, female: boolean, skin: { skin: string; skinS: string }, getter: (q: Piece) => boolean): Piece[] {
  if (!live()) return [...pieces];
  const j = sk.joints;
  const sides: Side[] = ['B', 'A'];
  let k = 0;
  let prev: Piece | null = null;
  const col: Record<string, string> = { s: skin.skin, t: shade(skin.skin, 0.03), S: skin.skinS, k: shade(skin.skin, -0.035), e: shade(skin.skin, -0.06) };
  return pieces.map((q) => {
    if (!getter(q) || q.hand) {
      prev = null;
      return q;
    }
    const isHand = prev !== null && Math.abs(q.z - (prev as Piece).z - 0.08) < 1e-6;
    prev = q;
    if (!isHand) return q;
    const side = sides[k++] ?? 'A';
    if (sk.pose !== 'idle' || !hanging(j, side)) return q;
    const grid = female ? HAND_F : HAND_M;
    const w = j[`wrist${side}`];
    const el = j[`elbow${side}`];
    const sh = j[`shoulder${side}`];
    const inner = sk.view === 'side' ? 1 : Math.sign(j.pelvis.x - w.x) || 1;
    const W = grid[0]!.length;
    // Where the forearm actually ends (newBody.ts armParts + figure.ts limb): its last art row is the wrist's, a hanging
    // forearm kept flush with its outer edge (`armOuter`), its widest run round(5 + L). The hand starts a row inside
    // that end and lies flush with the same outer edge, so it never floats off the arm (user 10-11: "hands float").
    const vertical = Math.abs(w.y - el.y) > Math.abs(w.x - el.x) * 1.5;
    const od = sh.x - j.chest.x;
    const outer = vertical ? (od < -0.5 ? -1 : od > 0.5 ? 1 : 0) : 0;
    const Wa = Math.max(1, Math.round(5 + sk.shape.limb));
    const wa = Math.max(1, Math.round(2.2));
    const X = Math.round(w.x);
    const left = outer < 0 ? X - Math.floor(Wa / 2) : outer > 0 ? X - Math.floor(Wa / 2) + Wa - wa : X - Math.floor(wa / 2);
    // Fine columns: flush with the forearm's outer edge (or centred on it).
    const fx0 = outer > 0 ? (left + wa) * 2 - W : outer < 0 ? left * 2 : left * 2 + wa - Math.floor(W / 2);
    const y0 = (Math.round(w.y) - 1) * 2;
    // Fine cells of the hand: column 0 on the inner (thumb) side.
    const cells: [number, number, string][] = [];
    grid.forEach((row, r) => {
      for (let c = 0; c < W; c++) {
        const ch = row[c]!;
        if (ch === '.') continue;
        const x = inner > 0 ? fx0 + W - 1 - c : fx0 + c;
        cells.push([x, y0 + r, col[ch]!]);
      }
    });
    const on = new Set(cells.map(([x, y]) => `${x},${y}`));
    return {
      get z() {
        return q.z;
      },
      hand: true,
      draw: (g) => {
        const done = new Set<string>();
        for (const [x, y] of cells) {
          const ax = Math.floor(x / 2);
          const ay = Math.floor(y / 2);
          const key = `${ax},${ay}`;
          if (done.has(key)) continue;
          done.add(key);
          let n = 0;
          for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]] as const) if (on.has(`${ax * 2 + dx},${ay * 2 + dy}`)) n++;
          if (n >= 2) {
            g.fillStyle = skin.skin;
            g.fillRect(ax, ay, 1, 1);
          }
        }
      },
      fine: (g, res) => {
        if (res !== 2) return;
        for (const [x, y, c] of cells) {
          g.fillStyle = c;
          g.fillRect(x, y, 1, 1);
        }
      },
    };
  });
}

/**
 * BODY-B3 R4 W1: background enclosed by the figure below the waist (a 2-3 px gap between thighs or knees, ringed by the
 * outline, read as a black oval hole) closed as a seam: the hole and its dark ring take the skin's shade, so the legs
 * read as touching. Larger openings (a real gap down to the feet) touch the frame's open background and are kept.
 * `minY`: the first canvas row checked (fine px). Review mock only.
 */
export function fillHoles(c: HTMLCanvasElement, minY: number, colour: string): void {
  if (!live()) return;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  const W = c.width;
  const H = c.height;
  const img = g.getImageData(0, 0, W, H);
  const d = img.data;
  const clear = (i: number) => d[i * 4 + 3]! === 0;
  const outside = new Uint8Array(W * H);
  const stack: number[] = [];
  for (let x = 0; x < W; x++) for (const y of [0, H - 1]) if (clear(y * W + x)) stack.push(y * W + x);
  for (let y = 0; y < H; y++) for (const x of [0, W - 1]) if (clear(y * W + x)) stack.push(y * W + x);
  while (stack.length) {
    const i = stack.pop()!;
    if (outside[i] || !clear(i)) continue;
    outside[i] = 1;
    const x = i % W;
    if (x > 0) stack.push(i - 1);
    if (x < W - 1) stack.push(i + 1);
    if (i >= W) stack.push(i - W);
    if (i < W * (H - 1)) stack.push(i + W);
  }
  const n = parseInt(colour.slice(1, 7), 16);
  const rgb = [n >> 16, (n >> 8) & 255, n & 255] as const;
  const seen = new Uint8Array(W * H);
  let changed = false;
  for (let start = minY * W; start < W * H; start++) {
    if (outside[start] || seen[start] || !clear(start)) continue;
    const comp: number[] = [];
    stack.push(start);
    while (stack.length) {
      const i = stack.pop()!;
      if (seen[i] || outside[i] || !clear(i)) continue;
      seen[i] = 1;
      comp.push(i);
      const x = i % W;
      if (x > 0) stack.push(i - 1);
      if (x < W - 1) stack.push(i + 1);
      if (i >= W) stack.push(i - W);
      if (i < W * (H - 1)) stack.push(i + W);
    }
    // Pin-holes and small slits (the crotch under the drawers' hems, a sliver between overlapping legs); a larger
    // opening is left to the stance.
    if (comp.length > 40) continue;
    const ring = new Set<number>();
    for (const i of comp) {
      const x = i % W;
      for (const dy of [-W, 0, W])
        for (const dx of [-1, 0, 1]) {
          if ((dx < 0 && x === 0) || (dx > 0 && x === W - 1)) continue;
          const k = i + dy + dx;
          if (k < 0 || k >= W * H || clear(k)) continue;
          const lum = d[k * 4]! * 0.3 + d[k * 4 + 1]! * 0.59 + d[k * 4 + 2]! * 0.11;
          if (lum < 70) ring.add(k);
        }
    }
    // The fill takes the commonest light colour just outside the ring (the legs' own skin or cloth: BODY dressed-r3 E4,
    // a fixed shade read as an orange bracket under the drawers), else the given shade.
    const votes = new Map<number, number>();
    const all = new Set([...comp, ...ring]);
    for (const i of ring) {
      const x = i % W;
      for (const k of [i - 1, i + 1, i - W, i + W]) {
        if (k < 0 || k >= W * H || all.has(k) || clear(k) || (x === 0 && k === i - 1) || (x === W - 1 && k === i + 1)) continue;
        const lum = d[k * 4]! * 0.3 + d[k * 4 + 1]! * 0.59 + d[k * 4 + 2]! * 0.11;
        if (lum < 70) continue;
        const key = (d[k * 4]! << 16) | (d[k * 4 + 1]! << 8) | d[k * 4 + 2]!;
        votes.set(key, (votes.get(key) ?? 0) + 1);
      }
    }
    let fill: readonly [number, number, number] = rgb;
    let most = 0;
    for (const [key, n] of votes) if (n > most) [most, fill] = [n, [key >> 16, (key >> 8) & 255, key & 255]];
    for (const i of all) {
      d[i * 4] = fill[0];
      d[i * 4 + 1] = fill[1];
      d[i * 4 + 2] = fill[2];
      d[i * 4 + 3] = 255;
    }
    changed = true;
  }
  // Lone outline pixels inside the figure (where two legs almost meet the finish rings a gap one pixel wide, leaving a
  // dark dot): small groups of dark pixels with no open background next to them take the shade too.
  const dark = (i: number) => d[i * 4 + 3]! > 0 && d[i * 4]! * 0.3 + d[i * 4 + 1]! * 0.59 + d[i * 4 + 2]! * 0.11 < 45;
  const near = (i: number) => {
    const x = i % W;
    for (const dy of [-W, 0, W])
      for (const dx of [-1, 0, 1]) {
        if ((dx < 0 && x === 0) || (dx > 0 && x === W - 1)) continue;
        const k = i + dy + dx;
        if (k >= 0 && k < W * H && outside[k]) return true;
      }
    return false;
  };
  const done = new Uint8Array(W * H);
  for (let start = minY * W; start < W * H; start++) {
    if (done[start] || !dark(start)) continue;
    const comp: number[] = [];
    let open = false;
    stack.push(start);
    while (stack.length) {
      const i = stack.pop()!;
      if (done[i] || !dark(i)) continue;
      done[i] = 1;
      comp.push(i);
      if (near(i)) open = true;
      const x = i % W;
      if (x > 0) stack.push(i - 1);
      if (x < W - 1) stack.push(i + 1);
      if (i >= W) stack.push(i - W);
      if (i < W * (H - 1)) stack.push(i + W);
    }
    if (open || comp.length > 24) continue;
    for (const i of comp) {
      d[i * 4] = rgb[0];
      d[i * 4 + 1] = rgb[1];
      d[i * 4 + 2] = rgb[2];
    }
    changed = true;
  }
  if (changed) g.putImageData(img, 0, 0);
}

/**
 * BODY-B3 R4 W4: turned three-quarter, the man's torso gets a 1-px lighter step down the lit (right) side of the chest
 * (from behind, the shoulder blade's), so the slab reads as a rotated torso. Review mock only.
 */
export function mockExtras(sk: Skeleton, female: boolean, skin: { skin: string; skinS: string }): Piece[] {
  if (!live() || female || !(sk.view === 'front3' || sk.view === 'back3')) return [];
  const j = sk.joints;
  return [
    {
      z: j.chest.z + 0.0005,
      draw: (g) => {
        const y0 = Math.round(j.chest.y) + 2;
        const y1 = Math.round(j.waist.y) - 2;
        // A soft plane edge two pixels wide, one step lighter (R5-3: a 1-px line read as a crack).
        const lit = shade(skin.skin, 0.025);
        for (let y = y0; y <= y1; y++) {
          const [, R] = torsoEdges(sk, 0, y);
          g.fillStyle = lit;
          g.fillRect(R - 4, y, 2, 1);
        }
      },
    },
  ];
}

/**
 * BODY-B3 R5-1: a woman's far leg, turned three-quarter, in a darker plane (its skin a step toward the shade), so with
 * the near leg over it the two legs read apart instead of one column. Review mock only; `isLeg` tells a leg piece.
 */
export function mockFarLeg(q: Piece, sk: Skeleton, female: boolean, skin: { skin: string; skinS: string }, isLeg: boolean): Piece {
  if (!live() || !female || !isLeg || sk.pose !== 'idle' || !(sk.view === 'front3' || sk.view === 'back3')) return q;
  const j = sk.joints;
  if (q.z !== Math.min(j.hipA.z, j.hipB.z)) return q;
  const lit = skin.skin.toLowerCase();
  const shadeTone = skin.skinS.toLowerCase();
  // On the skin's own ramp (R6-1: shade() pushed the tone to a saturated orange that read as a bruise): the lit tone a
  // step toward the skin's shadow, the shade the skin's own shadow.
  const mixHex = (a: string, c: string, k: number) =>
    `#${[1, 3, 5].map((i) => Math.round(parseInt(a.slice(i, i + 2), 16) * (1 - k) + parseInt(c.slice(i, i + 2), 16) * k).toString(16).padStart(2, '0')).join('')}`;
  const toS = mixHex(skin.skin, skin.skinS, 0.4);
  const deep = skin.skinS;
  return {
    ...q,
    draw: (g) => {
      let want = '';
      const proxy = new Proxy(g, {
        set(t, p, v) {
          if (p === 'fillStyle' && typeof v === 'string') want = v;
          (t as unknown as Record<string | symbol, unknown>)[p] = v;
          return true;
        },
        get(t, p) {
          if (p === 'fillRect')
            return (x: number, y: number, w: number, h: number) => {
              // Only the skin's own tones (a robe's cloth drawn on the leg keeps its colours).
              const tone = want.toLowerCase();
              if (tone === lit) t.fillStyle = toS;
              else if (tone === shadeTone) t.fillStyle = deep;
              t.fillRect(x, y, w, h);
              if (want) t.fillStyle = want;
            };
          const v = (t as unknown as Record<string | symbol, unknown>)[p];
          return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(t) : v;
        },
      });
      q.draw(proxy);
    },
  };
}

/** Garment layers whose cut follows the build's widths (a coat or robe over the torso). */
const CUT = new Set(['top', 'robe', 'cape', 'stole', 'belt']);

/**
 * BODY dressed-r1 D1-D3 (docs/review/art/2026-10-11-body-dressed-r1.md): the skeleton a garment is cut to, with the mock
 * on. The garments draw from the build's half-widths (figure.ts `torsoWidths`), so their cut is narrowed here, the body
 * underneath kept: in profile a coat stands at most ~4 px proud of the deeper mock torso and tapers to the belt (man and
 * woman); head-on and turned a woman's robe pinches at the waist and its hips (and so the hem's flare) come in, so the
 * sexes read apart in the long uniforms too. Mock only; every other figure and outfit is cut as before.
 */
export function mockGarment(sk: Skeleton, female: boolean, layer: string): Skeleton {
  if (!live() || !CUT.has(layer)) return sk;
  const sh = sk.shape;
  let d: readonly [number, number, number];
  if (sk.view === 'side') d = female ? [-1.6, -1.8, -0.6] : [-2.2, -3.6, -1.2];
  else if (female) d = [0, -1.6, -1.4];
  else return sk;
  return { ...sk, shape: { ...sh, shoulder: sh.shoulder + d[0], waist: sh.waist + d[1], hip: sh.hip + d[2] } };
}

/**
 * BODY dressed-r1 D6 / BODY-B3-R1 W9: a woman's long back hair, seen from behind, clipped to a narrower slab below the
 * shoulders (about 20 fine px) that tapers to a V tip at the waist, so the waist pinch shows each side. Mock only.
 */
export function mockHairBack(q: Piece, sk: Skeleton, female: boolean): Piece {
  if (!live() || !female || !sk.back) return q;
  const j = sk.joints;
  const cx = j.head.x;
  const y0 = Math.round(j.chest.y) + 2;
  const tip = Math.round(j.waist.y) + 2;
  // The head's art is laid in with putImageData (figureB3.ts `shrunk`), which a clip does not stop: the rows below the
  // shoulders are kept from before the draw wherever they fall outside the slab.
  const allowed = (x: number, y: number) => {
    if (y < y0) return true;
    if (y > tip) return false;
    const t = Math.max(0, Math.min(1, (y - (tip - 8)) / 8));
    const half = 5 - 4.5 * t;
    return Math.abs(x - cx) <= half + 0.5;
  };
  return {
    ...q,
    draw: (g) => {
      const tr = g.getTransform();
      const s = tr.a || 1;
      const X0 = Math.max(0, Math.floor(tr.e + (cx - 30) * s));
      const Y0 = Math.max(0, Math.floor(tr.f + y0 * s));
      const W = Math.min(g.canvas.width - X0, Math.ceil(60 * s));
      const H = Math.min(g.canvas.height - Y0, Math.ceil(90 * s));
      if (W <= 0 || H <= 0) return q.draw(g);
      const before = g.getImageData(X0, Y0, W, H);
      q.draw(g);
      const after = g.getImageData(X0, Y0, W, H);
      const b = new Uint32Array(before.data.buffer);
      const a = new Uint32Array(after.data.buffer);
      for (let py = 0; py < H; py++)
        for (let px = 0; px < W; px++) {
          const ax = (X0 + px - tr.e) / s;
          const ay = (Y0 + py - tr.f) / s;
          if (!allowed(ax, ay)) a[py * W + px] = b[py * W + px]!;
        }
      g.putImageData(after, X0, Y0);
    },
  };
}
